import type { ErrorDetail } from './types';
import { curlText } from './typography';

// Bundle-rebuild trigger: 2026-04-20 (landing curtain + iris transition).
const TOKEN_KEY = 'scotus_token';

// -- Toast surface (for Rate-limit + other UX errors) ---------------
export interface ToastLike {
  message: string;
  kind: 'info' | 'success' | 'warn' | 'error';
  durationMs?: number;
}
type ToastHandler = (t: ToastLike) => void;
let toastHandler: ToastHandler | null = null;
export function setToastHandler(fn: ToastHandler | null): void {
  toastHandler = fn;
}

// -- Token storage ---------------------------------------------------

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export function _resetForTesting(): void {
  clearToken();
}

// -- Error classes ---------------------------------------------------

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown> | undefined;

  constructor(status: number, detail: ErrorDetail) {
    super(detail.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = detail.code;
    this.details = detail.details ?? undefined;
  }
}

export class UnauthorizedError extends ApiError { constructor(s: number, d: ErrorDetail) { super(s, d); this.name = 'UnauthorizedError'; } }
export class ForbiddenError  extends ApiError { constructor(s: number, d: ErrorDetail) { super(s, d); this.name = 'ForbiddenError'; } }
export class NotFoundError   extends ApiError { constructor(s: number, d: ErrorDetail) { super(s, d); this.name = 'NotFoundError'; } }
export class ConflictError   extends ApiError { constructor(s: number, d: ErrorDetail) { super(s, d); this.name = 'ConflictError'; } }
// What counsel is told when createGame loses the race for the lectern. It
// lives beside the error rather than at either call site because there are
// two of them (the docket and the case brief) and they had drifted before:
// the sentence has to change with what the 409 means, and whoever changes
// that meaning is reading this file.
export const CREATE_GAME_CONFLICT_MESSAGE =
  'Another case was starting at the same moment. Try again.';
/** What counsel is told when createGame is refused with a 429 (F7). */
export function createGameRateLimitedMessage(err: unknown): string {
  return `The Court cannot take a new argument just now. Try again ${waitPhrase(retryAfterOf(err))}.`;
}
export class ValidationError extends ApiError { constructor(s: number, d: ErrorDetail) { super(s, d); this.name = 'ValidationError'; } }

export class RateLimitedError extends ApiError {
  readonly retryAfterSeconds: number | null;
  // Audit S1-4: the anon-session 429 carries ``limit_type`` so the UI
  // can render a precise message ("You've reached the daily limit of
  // 20 anonymous sessions per IP…") instead of the generic
  // "Rate limited, retry in Ns". Null on legacy / generic 429 bodies.
  readonly limitType: string | null;
  constructor(s: number, d: ErrorDetail, retryAfter: string | null) {
    super(s, d);
    this.name = 'RateLimitedError';
    this.retryAfterSeconds = retryAfter === null ? null : Number.parseInt(retryAfter, 10);
    // ``limit_type`` is on the error detail body, alongside ``code`` and
    // ``message``. The ErrorDetail interface only declares the shared
    // fields; we read it permissively here so older servers that don't
    // emit it leave limitType null.
    const lt = (d as unknown as { limit_type?: unknown }).limit_type;
    this.limitType = typeof lt === 'string' && lt.length > 0 ? lt : null;
  }
}

/**
 * How long to wait, in words, from a Retry-After header in seconds (F4).
 *
 * Every REST 429 carries Retry-After. The copy used to say "wait a minute"
 * whatever it said, while the anonymous-seat budget it was describing is five
 * an hour and twenty a day, so a player who waited the minute was refused
 * again and told the same thing.
 */
export function waitPhrase(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return 'in a little while';
  if (seconds < 60) {
    const s = Math.ceil(seconds);
    return s === 1 ? 'in a second' : `in ${s} seconds`;
  }
  const minutes = Math.ceil(seconds / 60);
  if (minutes < 60) return minutes === 1 ? 'in a minute' : `in about ${minutes} minutes`;
  const hours = Math.round(seconds / 3600);
  if (hours < 24) return hours <= 1 ? 'in about an hour' : `in about ${hours} hours`;
  return 'tomorrow';
}

/** The Retry-After of a 429, in seconds, or null when there is none. */
export function retryAfterOf(err: unknown): number | null {
  if (err instanceof RateLimitedError) {
    const s = err.retryAfterSeconds;
    return s !== null && Number.isFinite(s) ? s : null;
  }
  return null;
}

export class PausedError        extends ApiError { constructor(s: number, d: ErrorDetail) { super(s, d); this.name = 'PausedError'; } }
/** The request never reached a server: offline, DNS, TLS, a dropped link. */
export class NetworkError       extends ApiError { constructor(d: ErrorDetail) { super(0, d); this.name = 'NetworkError'; } }
export class InvalidPhaseError  extends ApiError { constructor(s: number, d: ErrorDetail) { super(s, d); this.name = 'InvalidPhaseError'; } }
export class ServerError        extends ApiError { constructor(s: number, d: ErrorDetail) { super(s, d); this.name = 'ServerError'; } }

function wrapError(status: number, detail: ErrorDetail, retryAfter: string | null): ApiError {
  switch (detail.code) {
    case 'unauthorized':      return new UnauthorizedError(status, detail);
    case 'forbidden':         return new ForbiddenError(status, detail);
    case 'not_found':         return new NotFoundError(status, detail);
    case 'conflict':          return new ConflictError(status, detail);
    case 'validation_failed': return new ValidationError(status, detail);
    case 'rate_limited':      return new RateLimitedError(status, detail, retryAfter);
    case 'paused':            return new PausedError(status, detail);
    case 'invalid_phase':     return new InvalidPhaseError(status, detail);
    case 'server_error':      return new ServerError(status, detail);
    case 'network':           return new NetworkError(detail);
    default:                  return new ApiError(status, detail);
  }
}

// -- Core request wrapper -------------------------------------------

export type Method = 'GET' | 'POST' | 'PATCH' | 'DELETE';

/**
 * What to tell the player when the server sent no words of its own.
 *
 * Every ApiError must carry a sentence somebody can read, and until now some
 * carried the empty string. ``resp.statusText`` has no reason phrase over
 * HTTP/2, which is what prod serves through Cloudflare, and a 502 or 503 from
 * a proxy is an HTML page rather than our JSON error envelope, so both
 * branches below fell through to ``''``. Every call site guards with
 * ``err instanceof Error ? err.message : 'something readable'`` and the error
 * really is an Error, so the ternary picked the empty string over its own
 * fallback, and ToastRack rendered a coloured border with a dismiss button
 * and nothing written in it. That is the "502s and 503s produce no
 * user-visible message" report (t3 finding #4).
 *
 * One function rather than a guard at each call site: there are dozens of
 * them, and the next one added would not know to guard.
 */
export function messageForStatus(status: number, statusText?: string): string {
  const given = (statusText ?? '').trim();
  if (given) return given;
  if (status === 502 || status === 503 || status === 504) {
    return 'The Court is not answering right now. Please try again in a moment.';
  }
  if (status >= 500) return 'Something went wrong on our side. Please try again in a moment.';
  if (status === 404) return 'That page is not on the server.';
  return `The request failed (${status}). Please try again.`;
}

export interface RequestOptions {
  /**
   * False when the caller explains a 429 itself. The global toast and the
   * caller's own message used to both fire, so a declined game start or
   * sign-in said the same thing twice in two places (F4, F7).
   */
  toastRateLimit?: boolean;
}

export async function request<T>(
  method: Method, path: string, body?: unknown, init?: RequestInit, opts: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init?.headers as Record<string, string> | undefined),
  };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  let resp: Response;
  try {
    resp = await fetch(path, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      ...init,
    });
  } catch {
    // fetch rejects, rather than resolving, when the request never reached a
    // server. Unwrapped, that surfaced as the browser's own "Failed to fetch"
    // or "NetworkError when attempting to fetch resource", which tells a
    // player nothing and differs per browser.
    throw new NetworkError({
      code: 'network',
      message:
        typeof navigator !== 'undefined' && navigator.onLine === false
          ? 'You are offline. Check your connection and try again.'
          : 'Could not reach the Court. Check your connection and try again.',
    });
  }

  if (resp.ok) {
    if (resp.status === 204) return undefined as T;
    return (await resp.json()) as T;
  }

  let detail: ErrorDetail;
  try {
    const payload = await resp.json() as { error?: ErrorDetail };
    detail = payload.error ?? { code: 'server_error', message: '' };
  } catch {
    detail = { code: 'server_error', message: '' };
  }
  // Whatever route we came by, the message has to say something. The server's
  // own sentence wins when it sent one.
  if (!(detail.message ?? '').trim()) {
    detail = { ...detail, message: messageForStatus(resp.status, resp.statusText) };
  }

  if (resp.status === 401) clearToken();
  const err = wrapError(resp.status, detail, resp.headers.get('Retry-After'));
  if (err instanceof RateLimitedError && toastHandler && opts.toastRateLimit !== false) {
    // Audit S1-4: prefer the server's specific message when we know the
    // limit type (e.g. "anon_session_per_ip"). Otherwise fall back to the
    // generic synthesized line so legacy 429 bodies still surface usefully.
    if (err.limitType) {
      const seconds = err.retryAfterSeconds ?? 0;
      toastHandler({
        message: err.message,
        kind: 'warn',
        // Long retry windows (e.g. 24h on anon-cap) shouldn't pin a toast
        // for 24h. Cap at 10s as before.
        durationMs: seconds > 0 ? Math.min(seconds * 1000, 10_000) : 10_000,
      });
    } else {
      const seconds = err.retryAfterSeconds ?? 30;
      toastHandler({
        message: `Too many requests. Try again ${waitPhrase(err.retryAfterSeconds)}.`,
        kind: 'warn',
        durationMs: Math.min(seconds * 1000, 10_000),
      });
    }
  }
  throw err;
}

// -- Typed endpoint wrappers ----------------------------------------

import type {
  Account, AccountPatch, AuthResponse, Device, MeResponse, Settings, SettingsPatch,
  Progress, Achievement, CaseSummary, CaseFull, Brief,
  CreateGameRequest, CreateGameResponse, GameSummary, GameFull,
  LeaderboardPage, MeRank, Metric, Window,
} from './types';

// Module-level promise cache for the docket preload. See api.getCases.
let _casesPromise: Promise<CaseSummary[]> | null = null;

// Endpoints whose every caller words its own 429 (the auth bootstrap and
// game creation), so the global toast stays out of it.
const QUIET_429: RequestOptions = { toastRateLimit: false };

export const api = {
  // Auth. `invite` (from a personalized ?invite=<token> link) binds the
  // new anonymous account to that invite for attribution. Omitted when
  // there's no invite token, so organic sessions send no body.
  postAnon: (invite?: string) =>
    request<AuthResponse>('POST', '/api/auth/anon', invite ? { invite } : undefined, undefined, QUIET_429),
  // The player UI has no code or email sign-in (2026-09-27); the server's
  // /api/auth/code and magic-link endpoints remain for existing named
  // accounts and the admin dashboard, but nothing here calls them.
  // Silent "never log in again" restore: the session of a browser holding a
  // named account's httpOnly device cookie (the cookie, not the IP, is the
  // credential), sent with credentials explicitly (a no-op same-origin,
  // needed when the dev SPA runs elsewhere). Resolves null (a 204) when this
  // browser is not remembered, the ordinary case, so it is not an error.
  // Quiet on 429: the bootstrap treats any failure as "not restored" and
  // carries on, so a toast would only alarm a visitor behind a busy shared
  // address about a call they never made (R3).
  postIpRestore: async (): Promise<AuthResponse | null> =>
    (await request<AuthResponse | undefined>('POST', '/api/auth/ip-restore', undefined, { credentials: 'include' }, QUIET_429)) ?? null,
  // One round trip for a first visit: restore a remembered browser, or else
  // open an anonymous session in the same request (session_kind: 'anon').
  // Same refusals as postAnon (403 gate, 429 budget).
  postIpRestoreOrAnon: async (): Promise<(AuthResponse & { session_kind?: 'anon' }) | null> =>
    (await request<(AuthResponse & { session_kind?: 'anon' }) | undefined>(
      'POST', '/api/auth/ip-restore?or_anon=1', undefined, { credentials: 'include' }, QUIET_429,
    )) ?? null,
  getMe: () => request<MeResponse>('GET', '/api/auth/me'),
  // Also clears the device cookie, so the next load is not restored.
  logout: () => request<void>('POST', '/api/auth/logout', undefined, { credentials: 'include' }),
  revokeDevice: (tokenHash: string) => request<void>('DELETE', `/api/auth/devices/${tokenHash}`),

  // Cases + briefs. `getCases` is promise-cached for one session so the
  // landing-page preload survives to the /home mount without a re-fetch.
  getCases: () => {
    if (!_casesPromise) {
      _casesPromise = request<CaseSummary[]>('GET', '/api/cases').then(curlText).catch((err) => {
        _casesPromise = null;  // allow retry on failure
        throw err;
      });
    }
    return _casesPromise;
  },
  preloadCases: () => {
    void api.getCases();
  },
  getCase: (id: string) => request<CaseFull>('GET', `/api/cases/${id}`).then(curlText),
  getBrief: (id: string) => request<Brief>('GET', `/api/cases/${id}/brief`).then(curlText),

  // Progress + achievements
  getProgress: () => request<Progress>('GET', '/api/progress'),
  getAchievements: () => request<Achievement[]>('GET', '/api/achievements').then(curlText),

  // Settings
  getSettings: () => request<Settings>('GET', '/api/settings'),
  patchSettings: (body: SettingsPatch) => request<Settings>('PATCH', '/api/settings', body),

  // Account (display_name, etc.). Separate from /settings because these
  // fields live on the `account` row itself rather than the settings
  // bundle, and the Chief Justice reads display_name out loud, so the
  // payload deserves its own purpose-built endpoint with stricter
  // validation (see backend AccountPatch).
  patchAccount: (body: AccountPatch) => request<Account>('PATCH', '/api/account', body),

  // Reset all play history + progression (games, achievements, XP, streaks),
  // keeping the account itself (email, display name, handle, preferences).
  resetAccount: () => request<Account>('POST', '/api/account/reset'),

  // Games
  // Both callers (the docket and the case brief) word the 429 themselves.
  createGame: (body: CreateGameRequest) =>
    request<CreateGameResponse>('POST', '/api/games', body, undefined, QUIET_429),
  getGame: (id: string) => request<GameFull>('GET', `/api/games/${id}`),
  // The unfinished case the player would most likely return to, or null: the
  // one at the lectern if there is one, else the most recently argued parked
  // case. For callers that need all of them, use getUnfinishedGames.
  getActiveGame: () => request<GameSummary | null>('GET', '/api/games/active'),
  // Every case started and not finished, likeliest first. Authoritative, and
  // the reason the docket does not guess the predicate itself: the old guess
  // (phase IN argument/conference/opinion) missed 'announcement', and a game
  // sitting there was invisible on the docket, so it could be neither
  // resumed nor abandoned (t3 finding #1).
  getUnfinishedGames: () => request<GameSummary[]>('GET', '/api/games/unfinished'),
  // ``offset`` pages the list in the server's own order. Prefer it to
  // ``before_ended_at``: that cursor filters on ended_at while the list is
  // ordered by COALESCE(ended_at, started_at), so it skips unfinished games
  // and same-second ties.
  listGames: (params: { limit?: number; offset?: number; before_ended_at?: number; phase?: string[] } = {}) => {
    const qs = new URLSearchParams();
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    if (params.offset !== undefined && params.offset > 0) qs.set('offset', String(params.offset));
    if (params.before_ended_at !== undefined) qs.set('before_ended_at', String(params.before_ended_at));
    if (params.phase && params.phase.length > 0) qs.set('phase', params.phase.join(','));
    const q = qs.toString();
    return request<GameSummary[]>('GET', q ? `/api/games?${q}` : '/api/games');
  },
  deleteGame: (id: string) => request<void>('DELETE', `/api/games/${id}`),
  // Step down from the lectern: park the game (paused, 7d retention) without
  // the standing penalty the in-argument Pause control carries.
  suspendGame: (id: string) => request<{ paused: boolean }>('POST', `/api/games/${id}/suspend`),
  shareGame: (id: string) => request<{ slug: string }>('POST', `/api/games/${id}/share`),
  getSharedGame: (slug: string) => request<GameFull>('GET', `/api/g/${slug}`),
  // The case behind a shared game, unauthenticated. getCase is account-gated
  // and every reader of a share link is logged out, so the share page cannot
  // use it without losing the caption, the docket and the seated bench.
  getSharedCase: (slug: string) => request<CaseFull>('GET', `/api/g/${slug}/case`),
  // The argument the landing page offers to readers who have not signed in.
  // `slug` is null when the server has nothing featured.
  getFeaturedArgument: () => request<{ slug: string | null }>('GET', '/api/featured-argument'),

  // Leaderboards
  getLeaderboard: (metric: Metric, window: Window, limit = 20, offset = 0) => {
    const qs = new URLSearchParams({ metric, window, limit: String(limit), offset: String(offset) });
    return request<LeaderboardPage>('GET', `/api/leaderboard?${qs}`);
  },
  getLeaderboardMe: (metric: Metric, window: Window) => {
    const qs = new URLSearchParams({ metric, window });
    return request<MeRank>('GET', `/api/leaderboard/me?${qs}`);
  },
} as const;

// Re-export types referenced by callers
export type { Account, Device, Settings };
